import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Controller,
  Resolver,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomerService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import { UserService } from "../user/service";
import DualListBox from "react-dual-listbox";
import "react-dual-listbox/lib/react-dual-listbox.css";
import {
  IContactPerson,
  ICustomer,
} from "../../shared/interfaces/customer/ICustomer";
import { IUser } from "../../shared/interfaces/user/IUser";
import CustomerPersonForm from "./CustomerPersonForm";
import Select from "react-select";
import LocalizedFileInput from "../../shared/LocalizedFileInput";
import GooglePlacesAutocomplete from "../../shared/components/GooglePlacesAutocomplete";
import { reverseGeocode, extractPostalCode } from "../../shared/utils/geocoding";
import {
  blockNegativeKey,
  blockNegativePaste,
  isNegativeInput,
} from "../../shared/utils/functions";
import { EnumsContext } from "../../context/EnumsContext";
import { CustomerTypeService } from "../customer-types/service";
import { ICustomerType } from "../../shared/interfaces/customer-type/ICustomerType";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

const customerSchema = yup.object().shape({
  userIds: yup.array().of(yup.number()).notRequired(),
  name: yup.string().required("validation.required"),
  lastName: yup.string().notRequired(),
  surname: yup.string().notRequired(),
  email: yup.string().email("Invalid email").required("validation.required"),
  phone: yup
    .string()
    .nullable()
    .notRequired()
    .test("phone-format", "validation.invalidPhone", function (value) {
      if (!value || value.trim() === "") return true; // Allow empty values
      return /^[\d+\s\-()\/]{6,20}$/.test(value);
    }),
  mobile: yup
    .string()
    .nullable()
    .notRequired()
    .test("mobile-format", "validation.invalidPhone", function (value) {
      if (!value || value.trim() === "") return true; // Allow empty values
      return /^[\d+\s\-()\/]{6,20}$/.test(value);
    }),
  featured: yup.boolean().notRequired(),
  additionalInfo: yup.string().notRequired(),
  contactPersons: yup
    .array()
    .of(
      yup.object({
        firstName: yup.string().nullable().optional(),
        surname: yup.string().nullable().optional(),
        lastName: yup.string().required("validation.required"),
        email: yup.string().email("Invalid email").nullable().optional(),
        phone: yup
          .string()
          .nullable()
          .matches(/^(?:[\d+\s\-()\/]{6,20})?$/, "validation.invalidPhone")
          .optional(),
      })
    )
    .optional(),
  salutation: yup.string().nullable().notRequired(),
  website: yup.string().url("Invalid URL").nullable().notRequired(),
  registrationNumber: yup.string().notRequired(),
  vatNumber: yup.string().notRequired(),
  customerType: yup.number().nullable().optional(),
  // A negative discount would add to the customer's price, not reduce it
  discount: yup
    .number()
    .nullable()
    .notRequired()
    .min(0, "validation.negativeDiscount"),
  sameAddress: yup.boolean().optional(),
  contact: yup
    .object({
      street: yup.string().nullable().notRequired(),
      zip: yup.string().nullable().notRequired(),
      city: yup.string().nullable().notRequired(),
    })
    .nullable()
    .notRequired(),
  billingAddress: yup
    .object({
      street: yup.string().nullable().notRequired(),
      zip: yup.string().nullable().notRequired(),
      city: yup.string().nullable().notRequired(),
    })
    .nullable()
    .notRequired(),
  logo: yup
    .mixed()
    .nullable()
    .notRequired()
    .test("fileType", "Unsupported file format", (value: any) => {
      if (!value) return true;
      return SUPPORTED_FORMATS.includes(value.type);
    }),
  cover: yup
    .mixed()
    .nullable()
    .notRequired()
    .test("fileType", "Unsupported file format", (value: any) => {
      if (!value) return true;
      return SUPPORTED_FORMATS.includes(value.type);
    }),
});
const CustomerForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [userOptions, setUserOptions] = useState<IUser[]>([]);
  const [customerTypes, setCustomerTypes] = useState<ICustomerType[]>([]);
  const { enums } = React.useContext(EnumsContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ICustomer>({
    defaultValues: {
      userIds: [],
      name: "",
      lastName: "",
      email: "",
      phone: "",
      mobile: "",
      featured: false,
      additionalInfo: "",
      contactPersons: [],
      salutation: enums?.salutation?.[0] || "",
      website: "",
      registrationNumber: "",
      vatNumber: "",
      customerType: undefined,
      company: false,
      sameAddress: true,
      discount: undefined,
      contact: {
        street: "",
        zip: "",
        city: "",
      },
      billingAddress: {
        street: "",
        zip: "",
        city: "",
      },
      logo: undefined,
      cover: undefined,
    },
    resolver: yupResolver(customerSchema) as unknown as Resolver<ICustomer>,
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const { company, files, sameAddress, contactPersons } = useWatch({ control });
  const isCompany = !!company;
  const coverImage = files?.find((file) => file.type === "COVER_TYPE");
  const logoImage = files?.find((file) => file.type === "LOGO_TYPE");

  const [showForms, setShowForms] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contactPersons",
  });

  const handleAddClick = () => {
    if (!showForms) {
      const isListEmpty = !contactPersons?.length;
      setShowForms(true);
      if (!isListEmpty) {
        return;
      }
    }
    append({ id: null } as unknown as IContactPerson);
  };
  const onSubmit = async (formValues: ICustomer) => {
    const isUpdate = !!formValues?.id;
    const formData = new FormData();
    formData.append(
      "body",
      JSON.stringify({
        ...formValues,
        logo: undefined,
        cover: undefined,
        users: undefined,
      })
    );
    formValues?.logo && formData.append("logo", formValues?.logo);
    formValues?.cover && formData.append("cover", formValues?.cover);

    const CorrectService = isUpdate
      ? CustomerService.update
      : CustomerService.create;

    CorrectService(formData, formValues?.id)
      .then(() => {
        SuccessToast(
          t(`customer.${isEdit ? "successUpdate" : "successCreate"}`)
        );
        navigate("/customers");
      })
      .catch(ErrorToast);
  };

  useEffect(() => {
    if (id) {
      const fetchCustomer = async () => {
        try {
          const response = await CustomerService.getSingle(+id!);
          reset({
            ...response,
            userIds: (response.users || [])?.map((u: IUser) => u.id),
            customerType: (response.customerType as unknown as ICustomerType)?.id ?? response.customerType,
          });
          if (response?.contactPersons) {
            setShowForms(true);
          }
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchCustomer();
    }
  }, [id]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await UserService.getAll({ page: 1, perPage: 1000 });
        setUserOptions(res.data);
      } catch (e) {
        ErrorToast(e);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    CustomerTypeService.getAll({ page: 1, perPage: 1000 })
      .then((res) => setCustomerTypes(res.data))
      .catch(ErrorToast);
  }, []);

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={12} className={"d-flex pb-3 mb-3 border-bottom"}>
                    <Form.Check
                      label={t("customer.private")}
                      name="customerType"
                      type="radio"
                      id="private"
                      className="me-5"
                      checked={!isCompany}
                      onChange={() => setValue("company", false)}
                    />
                    <Form.Check
                      label={t("customer.company")}
                      name="customerType"
                      type="radio"
                      id="company"
                      checked={isCompany}
                      onChange={() => setValue("company", true)}
                    />
                  </Col>
                </Row>
                <Row className="border-bottom mb-3">
                  <Col md={12}>
                    <h6 className={"pb-2"}>{t("general.basicInfo")}</h6>
                  </Col>
                  {!isCompany && (
                    <Col md={2}>
                      <Form.Group className="mb-3" controlId="salutation">
                        <Form.Label>{t("general.salutation")}</Form.Label>
                        <Controller
                          name="salutation"
                          control={control}
                          defaultValue={enums?.salutation?.[0] || ""}
                          render={({ field }) => (
                            <Select
                              classNamePrefix="react-select"
                              className={`react-select-container ${errors.salutation ? "is-invalid" : ""}`}
                              options={(enums?.salutation || []).map(
                                (item: string) => ({
                                  value: item,
                                  label: t(`salutation.${item}`),
                                })
                              )}
                              value={
                                (enums?.salutation || [])
                                  .map((item: string) => ({
                                    value: item,
                                    label: t(`salutation.${item}`),
                                  }))
                                  .find(
                                    (option: { value: string; label: string }) => option.value === field.value
                                  ) || null
                              }
                              onChange={(option) =>
                                field.onChange(
                                  option ? option.value : undefined
                                )
                              }
                              placeholder={t("general.selectSalutation")}
                              isClearable
                            />
                          )}
                        />
                        {errors.salutation && (
                          <div className="invalid-feedback d-block">
                            {t(errors.salutation?.message as string)}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="name">
                      {isCompany ? (
                        <Form.Label className="required">
                          {t("general.name")}
                        </Form.Label>
                      ) : (
                        <Form.Label className="required">
                          {t("general.firstName")}
                        </Form.Label>
                      )}
                      <Controller
                        control={control}
                        name="name"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!errors?.name?.message?.length}
                            placeholder={
                              isCompany
                                ? t("general.enterName")
                                : t("general.enterFirstName")
                            }
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.name?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {!isCompany && (
                    <Col md={3}>
                      <Form.Group className="mb-3" controlId="lastName">
                        <Form.Label>{t("general.lastName")}</Form.Label>
                        <Controller
                          control={control}
                          name="lastName"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Form.Control
                              onChange={onChange}
                              value={value}
                              isInvalid={!!errors?.lastName?.message?.length}
                              placeholder={t("general.enterLastName")}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t(errors.lastName?.message as string)}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  )}
                  {isCompany && (
                    <Col md={3}>
                      <Form.Group className="mb-3" controlId="customerType">
                        <Form.Label>{t("general.customerType")}</Form.Label>
                        <Controller
                          control={control}
                          name="customerType"
                          render={({ field }) => (
                            <Select
                              classNamePrefix="react-select"
                              className={`react-select-container ${errors.customerType ? "is-invalid" : ""}`}
                              options={customerTypes.map((item) => ({
                                value: item.id!,
                                label: item.name,
                              }))}
                              value={
                                customerTypes
                                  .map((item) => ({ value: item.id!, label: item.name }))
                                  .find((option) => option.value === field.value) || null
                              }
                              onChange={(option) =>
                                field.onChange(option ? option.value : undefined)
                              }
                              placeholder={t("validation.selectCustomerType")}
                              isClearable
                            />
                          )}
                        />
                        {errors.customerType && (
                          <div className="invalid-feedback d-block">
                            {t(errors.customerType?.message as string)}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={1}>
                    <Form.Group className="mb-3" controlId="featured">
                      <Form.Label>{t("customer.featured")}</Form.Label>
                      <Controller
                        control={control}
                        name="featured"
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                          <Form.Check
                            type="checkbox"
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.featured?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3" controlId="discount">
                      <Form.Label>{t("customer.discount")}</Form.Label>
                      <Controller
                        control={control}
                        name="discount"
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={(e) => {
                              if (isNegativeInput(e.target.value)) return;
                              onChange(e);
                            }}
                            onKeyDown={blockNegativeKey}
                            onPaste={blockNegativePaste}
                            value={value ?? ""}
                            type="number"
                            step="0.01"
                            min="0"
                            isInvalid={!!errors?.discount?.message?.length}
                            placeholder={t("customer.enterDiscount")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.discount?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3" controlId="surname">
                      <Form.Label>{t("customer.surname")}</Form.Label>
                      <Controller
                        control={control}
                        name="surname"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value ?? ""}
                            isInvalid={!!errors?.surname?.message?.length}
                            placeholder={t("customer.enterSurname")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.surname?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="border-bottom mb-3">
                  <Col md={12}>
                    <h6 className={"pb-2"}>{t("general.address")}</h6>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="street">
                      <Form.Label>{t("general.street")}</Form.Label>
                      <Controller
                        control={control}
                        name="contact.street"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <GooglePlacesAutocomplete
                            value={value}
                            onChange={onChange}
                            onPlaceSelect={async (place) => {
                              // Extract address components if available
                              if (place.addressComponents) {
                                // Extract city
                                const cityComponent =
                                  place.addressComponents.find(
                                    (component: any) =>
                                      component.types.includes("locality") ||
                                      component.types.includes(
                                        "administrative_area_level_1"
                                      ) ||
                                      component.types.includes("sublocality")
                                  );
                                if (cityComponent && cityComponent.long_name) {
                                  setValue(
                                    "contact.city",
                                    cityComponent.long_name
                                  );
                                }

                                // Extract ZIP code
                                const zipComponent =
                                  place.addressComponents.find(
                                    (component: any) =>
                                      component.types.includes("postal_code") ||
                                      component.types.includes("postal_code_prefix")
                                  );
                                if (zipComponent && zipComponent.long_name) {
                                  setValue(
                                    "contact.zip",
                                    zipComponent.long_name
                                  );
                                } else if (place.coordinates) {
                                  // Fallback: Do reverse geocoding to get postal code
                                  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                                  if (apiKey) {
                                    try {
                                      const reverseResult = await reverseGeocode(
                                        place.coordinates.lat,
                                        place.coordinates.lng,
                                        apiKey
                                      );
                                      if (reverseResult) {
                                        const postalCode = extractPostalCode(
                                          reverseResult.address_components
                                        );
                                        if (postalCode) {
                                          setValue("contact.zip", postalCode);
                                        } else {
                                          // Last fallback: try to extract ZIP from formatted address
                                          const zipMatch = place.address?.match(/\b(\d{4})\b/);
                                          if (zipMatch && zipMatch[1]) {
                                            setValue("contact.zip", zipMatch[1]);
                                          }
                                        }
                                      }
                                    } catch (error) {
                                      console.error("Reverse geocoding failed:", error);
                                      // Last fallback: try to extract ZIP from formatted address
                                      const zipMatch = place.address?.match(/\b(\d{4})\b/);
                                      if (zipMatch && zipMatch[1]) {
                                        setValue("contact.zip", zipMatch[1]);
                                      }
                                    }
                                  }
                                }
                              }
                            }}
                            placeholder={t("general.enterStreet")}
                            isInvalid={
                              !!errors?.contact?.street?.message?.length
                            }
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors?.contact?.street?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="zip">
                      <Form.Label>{t("general.zip")}</Form.Label>
                      <Controller
                        control={control}
                        name="contact.zip"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!errors?.contact?.zip?.message?.length}
                            placeholder={t("general.enterZip")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors?.contact?.zip?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="city">
                      <Form.Label>{t("general.city")}</Form.Label>
                      <Controller
                        control={control}
                        name="contact.city"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            type={"city"}
                            isInvalid={!!errors?.contact?.city?.message?.length}
                            placeholder={t("general.enterCity")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors?.contact?.city?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {/* Country dropdown removed as requested */}
                </Row>
                <Row className="border-bottom mb-3">
                  <Col md={12}>
                    <h6 className={"pb-2"}>{t("general.contact")}</h6>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label className="required">
                        {t("general.email")}
                      </Form.Label>
                      <Controller
                        control={control}
                        name="email"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            type={"email"}
                            isInvalid={!!errors?.email?.message?.length}
                            placeholder={t("general.enterEmail")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.email?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="contact.phone">
                      <Form.Label>{t("general.phone")}</Form.Label>
                      <Controller
                        control={control}
                        name="phone"
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            type={"tel"}
                            isInvalid={!!errors?.phone?.message?.length}
                            placeholder={t("general.enterPhone")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors?.phone?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="mobile">
                      <Form.Label>{t("general.mobile")}</Form.Label>
                      <Controller
                        control={control}
                        name="mobile"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            type={"tel"}
                            isInvalid={!!errors?.mobile?.message?.length}
                            placeholder={t("general.enterMobile")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors?.mobile?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {isCompany && (
                    <>
                      <Col md={3} className="d-flex align-items-center pt-2">
                        <Button
                          variant="primary"
                          type={"button"}
                          onClick={handleAddClick}
                        >
                          {t("general.addNewContactPerson")}
                        </Button>
                      </Col>
                      {showForms &&
                        fields?.map((item, index) => (
                          <CustomerPersonForm
                            key={item?.id ?? index}
                            index={index}
                            control={control}
                            remove={remove}
                          />
                        ))}
                    </>
                  )}
                </Row>
                {isCompany && (
                  <Row className="border-bottom mb-3">
                    <Col md={12}>
                      <h6 className={"pb-2"}>{t("general.companyInfo")}</h6>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="website">
                        <Form.Label>{t("general.website")}</Form.Label>
                        <Controller
                          control={control}
                          name="website"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Form.Control
                              onChange={onChange}
                              value={value}
                              type={"url"}
                              isInvalid={!!errors?.website?.message?.length}
                              placeholder={t("general.enterWebsite")}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t(errors.website?.message as string)}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group
                        className="mb-3"
                        controlId="registrationNumber"
                      >
                        <Form.Label>
                          {t("general.registrationNumber")}
                        </Form.Label>
                        <Controller
                          control={control}
                          name="registrationNumber"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Form.Control
                              onChange={onChange}
                              value={value}
                              type={"text"}
                              isInvalid={
                                !!errors?.registrationNumber?.message?.length
                              }
                              placeholder={t("general.enterRegistrationNumber")}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t(errors.registrationNumber?.message as string)}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="vatNumber">
                        <Form.Label>{t("general.vatNumber")}</Form.Label>
                        <Controller
                          control={control}
                          name="vatNumber"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Form.Control
                              onChange={onChange}
                              value={value}
                              type={"text"}
                              isInvalid={!!errors?.vatNumber?.message?.length}
                              placeholder={t("general.enterVatNumber")}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t(errors.vatNumber?.message as string)}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Row className="border-bottom mb-3">
                  <Col md={12}>
                    <h6 className={"pb-2"}>{t("general.billingAddress")}</h6>
                    <Controller
                      control={control}
                      name="sameAddress"
                      render={({ field: { onChange, value } }) => (
                        <Form.Check
                          onChange={onChange}
                          type="checkbox"
                          isInvalid={!!errors?.sameAddress?.message?.length}
                          placeholder={t("general.sameAsPersonalAddress")}
                          label={t("general.sameAsPersonalAddress")}
                          className={"mb-3"}
                          checked={sameAddress}
                        />
                      )}
                    />
                  </Col>
                  {!sameAddress ? (
                    <>
                      <Col md={3}>
                        <Form.Group className="mb-3" controlId="street">
                          <Form.Label>{t("general.street")}</Form.Label>
                          <Controller
                            control={control}
                            name="billingAddress.street"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <GooglePlacesAutocomplete
                                value={value}
                                onChange={onChange}
                                onPlaceSelect={async (place) => {
                                  // Extract address components if available
                                  if (place.addressComponents) {
                                    // Extract city
                                    const cityComponent =
                                      place.addressComponents.find(
                                        (component: any) =>
                                          component.types.includes(
                                            "locality"
                                          ) ||
                                          component.types.includes(
                                            "administrative_area_level_1"
                                          ) ||
                                          component.types.includes(
                                            "sublocality"
                                          )
                                      );
                                    if (
                                      cityComponent &&
                                      cityComponent.long_name
                                    ) {
                                      setValue(
                                        "billingAddress.city",
                                        cityComponent.long_name
                                      );
                                    }

                                    // Extract ZIP code
                                    const zipComponent =
                                      place.addressComponents.find(
                                        (component: any) =>
                                          component.types.includes(
                                            "postal_code"
                                          ) ||
                                          component.types.includes(
                                            "postal_code_prefix"
                                          )
                                      );
                                    if (
                                      zipComponent &&
                                      zipComponent.long_name
                                    ) {
                                      setValue(
                                        "billingAddress.zip",
                                        zipComponent.long_name
                                      );
                                    } else if (place.coordinates) {
                                      // Fallback: Do reverse geocoding to get postal code
                                      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                                      if (apiKey) {
                                        try {
                                          const reverseResult = await reverseGeocode(
                                            place.coordinates.lat,
                                            place.coordinates.lng,
                                            apiKey
                                          );
                                          if (reverseResult) {
                                            const postalCode = extractPostalCode(
                                              reverseResult.address_components
                                            );
                                            if (postalCode) {
                                              setValue("billingAddress.zip", postalCode);
                                            } else {
                                              // Last fallback: try to extract ZIP from formatted address
                                              const zipMatch = place.address?.match(/\b(\d{4})\b/);
                                              if (zipMatch && zipMatch[1]) {
                                                setValue("billingAddress.zip", zipMatch[1]);
                                              }
                                            }
                                          }
                                        } catch (error) {
                                          console.error("Reverse geocoding failed:", error);
                                          // Last fallback: try to extract ZIP from formatted address
                                          const zipMatch = place.address?.match(/\b(\d{4})\b/);
                                          if (zipMatch && zipMatch[1]) {
                                            setValue("billingAddress.zip", zipMatch[1]);
                                          }
                                        }
                                      }
                                    }
                                  }
                                }}
                                placeholder={t("general.enterStreet")}
                                isInvalid={
                                  !!errors?.billingAddress?.street?.message
                                    ?.length
                                }
                              />
                            )}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(
                              errors?.billingAddress?.street?.message as string
                            )}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3" controlId="zip">
                          <Form.Label>{t("general.zip")}</Form.Label>
                          <Controller
                            control={control}
                            name="billingAddress.zip"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Form.Control
                                onChange={onChange}
                                value={value}
                                isInvalid={
                                  !!errors?.billingAddress?.zip?.message?.length
                                }
                                placeholder={t("general.enterZip")}
                              />
                            )}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(errors?.billingAddress?.zip?.message as string)}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3" controlId="city">
                          <Form.Label>{t("general.city")}</Form.Label>
                          <Controller
                            control={control}
                            name="billingAddress.city"
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <Form.Control
                                onChange={onChange}
                                value={value}
                                type={"city"}
                                isInvalid={
                                  !!errors?.billingAddress?.city?.message
                                    ?.length
                                }
                                placeholder={t("general.enterCity")}
                              />
                            )}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(errors?.billingAddress?.city?.message as string)}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {/* Country dropdown removed as requested */}
                    </>
                  ) : (
                    <></>
                  )}
                </Row>

                <Row className="border-bottom mb-3">
                  <Col md={12}>
                    <h6 className={"pb-2"}>{t("general.fileUpload")}</h6>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="images">
                      <Form.Label>{t("customer.logo")} </Form.Label>
                      <Controller
                        name={`logo`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <>
                            <LocalizedFileInput
                              accept={"image/*"}
                              value={value as File | null | undefined}
                              onChange={(file) => onChange(file)}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(errors?.logo?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                    {logoImage?.id && (
                      <img
                        src={logoImage?.path}
                        alt={"Customer logo"}
                        className={"w-100 gallery-image mb-3"}
                      />
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="images">
                      <Form.Label>{t("customer.cover")} </Form.Label>
                      <Controller
                        name={`cover`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <>
                            <LocalizedFileInput
                              accept={"image/*"}
                              value={value as File | null | undefined}
                              onChange={(file) => onChange(file)}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(errors?.cover?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                    {coverImage?.id && (
                      <>
                        <img
                          src={coverImage?.path}
                          alt={"Customer logo"}
                          className={"w-100 cover-image mb-2"}
                        />
                        <Form.Text>{t("customer.coverDisclaimer")}</Form.Text>
                      </>
                    )}
                  </Col>
                </Row>

                <Row>
                  {/*{sameAddress && (*/}
                  <Col md={12}>
                    <Form.Group className="mb-3" controlId="additionalInfo">
                      <Form.Label>{t("general.additionalInfo")}</Form.Label>
                      <Controller
                        control={control}
                        name="additionalInfo"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            as="textarea"
                            rows={4}
                            onChange={onChange}
                            value={value ?? ""}
                            isInvalid={
                              !!errors?.additionalInfo?.message?.length
                            }
                            placeholder={t("general.enterAdditionalInfo")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.additionalInfo?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {/*)}*/}
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <h6 className={"pb-2"}>{t("customer.users")}</h6>
                  </Col>
                  <Col sm={12}>
                    <Controller
                      name="userIds"
                      control={control}
                      render={({ field }) => {
                        return (
                          // @ts-ignore
                          <DualListBox
                            options={userOptions}
                            selected={(
                              userOptions?.filter((user) =>
                                (field.value as number[])?.some(
                                  (id: number) => user.id === id
                                )
                              ) ?? []
                            )?.map((u) => u.id)}
                            onChange={field.onChange}
                            canFilter
                            getOptionValue={(item: IUser) => item.id}
                            getOptionLabel={(item: IUser) => {
                              const roleLabels = (item.roles || [])
                                .map((r) => t(`enums.userRole.${r}`))
                                .filter(Boolean);

                              const name = [item.firstName, item.lastName]
                                .filter(Boolean)
                                .join(" ");

                              const rolesText = roleLabels.join(", ");

                              return rolesText
                                ? `${name} - ${rolesText}`
                                : name;
                            }}
                            icons={{
                              moveToAvailable: <span>&lt;</span>,
                              moveAllToAvailable: <span>&laquo;</span>,
                              moveToSelected: <span>&gt;</span>,
                              moveAllToSelected: <span>&raquo;</span>,
                            }}
                            lang={{
                              availableHeader: t("customer.availableUsers"),
                              selectedHeader: t("customer.assignedUsers"),
                              // support multiple prop names/versions so both filters translate
                              availableFilterPlaceholder: t(
                                "customer.filterUsers"
                              ),
                              availableFilterPlaceholer: t(
                                "customer.filterUsers"
                              ),
                              filterPlaceholder: t("customer.filterUsers"),
                              selectedFilterPlaceholder: t(
                                "customer.filterUsers"
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`customer.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/customers")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default CustomerForm;
